import {
    Item,
    ItemConditionEnum,
    ItemGroupConfiguration,
    ItemHistory,
    ProductionFormula,
} from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { DataFetcherFunction } from '@/compiler/types';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';
import { ListingResponse, toListing } from '../channels/listings';
import { CompanyServiceResponse, toCompanyService } from '../companyServices';
import { ProductionFormulaResponse } from '../productionFormulas';
import { TagResponse, toTag } from '../tags';
import adjustmentsFactory from './adjustments';
import customLabelsFactory, { ItemCustomLabelRaw, toCustomLabel } from './customLabels';
import fixItemsFactory from './fixItems';
import identifiersFactory, { ItemIdentifierResponse, toItemIdentifier } from './identifiers';
import imagesFactory, { ImageResponse, toImage } from './images';
import reconcileFactory from './reconcile';
import searchFactory from './search';
import stocksFactory, { StockResponse, toStock } from './stocks';
import traceabilityGroupsFactory, { ItemTraceabilityGroupResponse, toItemTraceabilityGroup } from './traceabilityGroups';

export type { ImageResponse } from './images';
export type { ItemIdentifierResponse } from './identifiers';
export type { CompanyServiceResponse } from '../companyServices';
export type { TagResponse } from '../tags';
export type { ListingResponse } from '../channels/listings';
export type { ProductionFormulaResponse } from '../productionFormulas';

export interface ItemHistoryResponse {
    date: string
    type: string
    id: number | string
    quantity: number
    stock_status: 'complete' | 'inbound' | 'allocated'
    completed_date: string | null
}

export interface ItemGroupConfigurationResponse {
    id: number
    uuid: string
    created_at: string
    updated_at: string
    product_uuid: string
    name: string | null
    quantity: number
    type: 'box' | 'pallet'
    weight_gm: number
    length_mm: number
    height_mm: number
    width_mm: number
}

// The raw Eloquent item that other modules receive nested inside their own
// responses (listings, orders, production formulas, reconcile); no operation
// documents it, so it stays hand-written.
export interface ItemResponse {
    custom_label?: ItemCustomLabelRaw | null
    id: number
    uuid: string
    merchant_id: number
    listings?: ListingResponse[]
    title: string
    merchant_sku: string
    images?: ImageResponse[]
    identifiers?: ItemIdentifierResponse[]
    searchable_identifiers?: string
    item_group_configurations?: ItemGroupConfigurationResponse[]
    traceability_groups?: ItemTraceabilityGroupResponse[]
    company_services?: CompanyServiceResponse[]
    prep_instructions?: string[]
    cost_per_item?: number
    quantity_in_stock: number
    history?: ItemHistoryResponse[]
    weight_gm: number
    length_mm: number
    width_mm: number
    height_mm: number
    are_dimensions_locked: boolean | null
    deleted_at: string | null
    condition: ItemConditionEnum | null
    condition_note: string
    available_quantity?: number
    unavailable_quantity?: number
    allocated_quantity?: number
    inbound_quantity?: number
    searchable_tags?: string[]
    tags?: TagResponse[]
    stocks?: StockResponse[]
    production_formulas?: ProductionFormulaResponse[]
}

type GeneratedInventoryItem = components['schemas']['InventoryItem'];

export type ItemHistoryRaw = NonNullable<GeneratedInventoryItem['history']>[number];
export type ItemGroupConfigurationRaw = NonNullable<GeneratedInventoryItem['item_group_configurations']>[number];

export type InventoryItemRaw = Omit<GeneratedInventoryItem, 'production_formulas' | 'company_services' | 'tags'> & {
    // Present on list/detail only; absent from the published schema.
    custom_label?: ItemCustomLabelRaw | null
    // The spec types these relations with arrays of untyped objects. Return to
    // the generated fields once it documents their element schemas.
    production_formulas?: ProductionFormulaResponse[]
    company_services?: CompanyServiceResponse[]
    // toTag takes the tags module's TagResponse, whose type and colour are
    // TypeScript string enums that the generated string-literal unions cannot
    // be assigned to. Return to the generated field when the tags module
    // migrates.
    tags?: TagResponse[]
};

// Carries the corrected item into the operations that return inventory items.
export type WithInventoryItems<Response> = {
    [Key in keyof Response]: Response[Key] extends GeneratedInventoryItem
        ? InventoryItemRaw
        : Response[Key] extends GeneratedInventoryItem[] ? InventoryItemRaw[] : Response[Key]
};

export type FetchInventoryResponse = WithInventoryItems<operations['get-inventory']['responses']['200']['content']['application/json']>;
export type FetchInventoryItemResponse = WithInventoryItems<operations['get-inventory-item']['responses']['200']['content']['application/json']>;
export type CreateInventoryItemResponse = WithInventoryItems<operations['create-inventory-item']['responses']['201']['content']['application/json']>;
export type UpdateInventoryItemResponse = WithInventoryItems<operations['update-inventory-item']['responses']['200']['content']['application/json']>;
export type UpdateInventoryItemDimensionsLockResponse = WithInventoryItems<operations['update-inventory-item-dimensions-lock']['responses']['200']['content']['application/json']>;
export type TagInventoryItemResponse = operations['tag-inventory-item']['responses']['200']['content']['application/json'];
export type UntagInventoryItemResponse = operations['untag-inventory-item']['responses']['200']['content']['application/json'];
export type ManageInventoryItemServicesResponse = operations['manage-inventory-item-services']['responses']['200']['content']['application/json'];

export type CreateInventoryItemRequest = operations['create-inventory-item']['requestBody']['content']['application/json'];
// copyListingToItem forwards a listing's condition, which the spec types with a
// free string; the server validates it against the item conditions.
// Return to CreateInventoryItemRequest once that caller is typed.
type CreateInventoryItemBody = Omit<CreateInventoryItemRequest, 'condition'> & { condition?: string | null };
export type UpdateInventoryItemRequest = operations['update-inventory-item']['requestBody']['content']['application/json'];
export type UpdateInventoryItemDimensionsLockRequest = operations['update-inventory-item-dimensions-lock']['requestBody']['content']['application/json'];
export type ManageInventoryItemServicesRequest = operations['manage-inventory-item-services']['requestBody']['content']['application/json'];
export type BatchDeleteInventoryItemsRequest = operations['batch-delete-inventory-items']['requestBody']['content']['application/json'];

interface createInventoryItemFormInterface {
    title: string
    merchantSku: string
    condition?: string | null
    conditionNote?: string | null
    lengthMm?: number | null
    widthMm?: number | null
    heightMm?: number | null
    weightGm?: number | null
}

interface updateInventoryItemFormInterface {
    title?: string | null
    condition?: ItemConditionEnum | null
    conditionNote?: string | null
    lengthMm?: number | null
    widthMm?: number | null
    heightMm?: number | null
    weightGm?: number | null
}

function toItemGroupConfiguration(g: ItemGroupConfigurationResponse | ItemGroupConfigurationRaw): ItemGroupConfiguration {
    return {
        id: g.id,
        uuid: g.uuid,
        createdAt: g.created_at,
        updatedAt: g.updated_at,
        productUuid: g.product_uuid,
        name: g.name,
        quantity: g.quantity,
        type: g.type,
        weightGm: g.weight_gm,
        lengthMm: g.length_mm,
        heightMm: g.height_mm,
        widthMm: g.width_mm,
    };
}

function toItemHistory(h: ItemHistoryResponse | ItemHistoryRaw): ItemHistory {
    return {
        date: h.date,
        type: h.type,
        id: h.id,
        quantity: h.quantity,
        stockStatus: h.stock_status,
        completedDate: h.completed_date,
    };
}

function toProductionFormula(pf: ProductionFormulaResponse): ProductionFormula {
    return {
        uuid: pf.uuid,
        ownerUuid: pf.owner_uuid,
        name: pf.name,
        description: pf.description,
        inputServiceLines: pf.input_service_lines.map(sl => ({
            serviceUuid: sl.service_uuid,
            quantity: sl.quantity,
            ordering: sl.ordering,
        })),
        inputProductLines: pf.input_product_lines.map(pl => ({
            productUuid: pl.product_uuid,
            quantity: pl.quantity,
            ordering: pl.ordering,
            product: toItem(pl.product),
        })),
        outputProductLines: pf.output_product_lines.map(pl => ({
            productUuid: pl.product_uuid,
            quantity: pl.quantity,
            ordering: pl.ordering,
            product: toItem(pl.product),
        })),
        quantityProducible: pf.quantity_producible,
        createdAt: pf.created_at,
        updatedAt: pf.updated_at,
    };
}

// Only the index appends the documented camelCase key. The nested shape keeps
// its snake_case key, which the server never sends, so nested items map to ''.
function toSearchableIdentifiers(inv: ItemResponse | InventoryItemRaw): string {
    if ('searchableIdentifiers' in inv && inv.searchableIdentifiers !== undefined) return inv.searchableIdentifiers;
    if ('searchable_identifiers' in inv && inv.searchable_identifiers !== undefined) return inv.searchable_identifiers;

    return '';
}

// Serves both this module's documented item and the nested raw item other
// modules receive; the fields only the nested shape declares are never sent
// by the inventory operations and map exactly like an absent field.
export function toItem(inv: ItemResponse | InventoryItemRaw): Item;
export function toItem(inv: ItemResponse | InventoryItemRaw | null): Item | null;
export function toItem(inv: ItemResponse | InventoryItemRaw | null): Item | null {
    if (!inv) return null;

    return {
        id: inv.id,
        uuid: inv.uuid,
        merchantId: inv.merchant_id,
        listings: 'listings' in inv ? inv.listings?.map(toListing) : undefined,
        title: inv.title,
        merchantSku: inv.merchant_sku,
        images: (inv.images ?? []).map(toImage),
        identifiers: (inv.identifiers ?? []).map(toItemIdentifier),
        ...(inv.custom_label === undefined ? {} : { customLabel: inv.custom_label === null ? null : toCustomLabel(inv.custom_label) }),
        searchableIdentifiers: toSearchableIdentifiers(inv),
        itemGroupConfigurations: (inv.item_group_configurations ?? []).map(toItemGroupConfiguration),
        traceabilityGroups: inv.traceability_groups?.map(tg => toItemTraceabilityGroup(tg)),
        companyServices: (inv.company_services ?? []).map(toCompanyService),
        prepInstructions: 'prep_instructions' in inv ? inv.prep_instructions ?? [] : [],
        costPerItem: 'cost_per_item' in inv ? inv.cost_per_item : undefined,
        quantityInStock: inv.quantity_in_stock,
        history: inv.history?.map(toItemHistory),
        weightGm: inv.weight_gm,
        lengthMm: inv.length_mm,
        widthMm: inv.width_mm,
        heightMm: inv.height_mm,
        areDimensionsLocked: inv.are_dimensions_locked,
        deletedAt: inv.deleted_at,
        condition: inv.condition,
        conditionNote: inv.condition_note,
        availableQuantity: inv.available_quantity,
        unavailableQuantity: inv.unavailable_quantity,
        allocatedQuantity: inv.allocated_quantity,
        inboundQuantity: inv.inbound_quantity,
        searchableTags: 'searchable_tags' in inv ? inv.searchable_tags ?? [] : [],
        tags: inv.tags?.map(toTag),
        stocks: inv.stocks?.map(s => toStock(s)),
        productionFormulas: inv.production_formulas?.map(toProductionFormula),
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): {
    customLabels: ReturnType<typeof customLabelsFactory>
    adjustments: ReturnType<typeof adjustmentsFactory>
    identifiers: ReturnType<typeof identifiersFactory>
    traceabilityGroups: ReturnType<typeof traceabilityGroupsFactory>
    images: ReturnType<typeof imagesFactory>
    fixItems: ReturnType<typeof fixItemsFactory>
    reconcile: ReturnType<typeof reconcileFactory>
    search: ReturnType<typeof searchFactory>
    stocks: ReturnType<typeof stocksFactory>
    fetchItems: (preRequest?: (uri: URL) => URL) => DataFetcherFunction<Item>
    fetchInventoryItem: (itemId: number) => Promise<Item>
    createInventoryItem: (request: createInventoryItemFormInterface) => Promise<Item>
    updateInventoryItem: (itemId: number, request: updateInventoryItemFormInterface) => Promise<Item>
    updateInventoryItemAreDimensionsLocked: (itemId: number, areDimensionsLocked: boolean) => Promise<Item>
    tagInventoryItem: (itemId: number, tagId: number) => Promise<void>
    untagInventoryItem: (itemId: number, tagId: number) => Promise<void>
    manageServices: (itemId: number, serviceIds: number[]) => Promise<void>
} => ({
    customLabels: customLabelsFactory(axios),
    adjustments: adjustmentsFactory(axios),
    identifiers: identifiersFactory(axios),
    traceabilityGroups: traceabilityGroupsFactory(axios),
    images: imagesFactory(axios),
    fixItems: fixItemsFactory(axios),
    reconcile: reconcileFactory(axios),
    search: searchFactory(axios),
    stocks: stocksFactory(axios),

    fetchItems: (preRequest?: (uri: URL) => URL): DataFetcherFunction<Item> =>
        fetchPaginatedDataFunctionWithMapper<FetchInventoryResponse['data'][number], Item>('/api/inventory', toItem, preRequest, axios),

    fetchInventoryItem: async (itemId: number): Promise<Item> => {
        const response = await axios.get<FetchInventoryItemResponse>(`/api/inventory/${itemId}`);

        return toItem(response.data.item);
    },

    createInventoryItem: async (request: createInventoryItemFormInterface): Promise<Item> => {
        const body: CreateInventoryItemBody = {
            title: request.title,
            merchant_sku: request.merchantSku,
            condition: request.condition,
            condition_note: request.conditionNote,
            length_mm: request.lengthMm,
            width_mm: request.widthMm,
            height_mm: request.heightMm,
            weight_gm: request.weightGm,
        };
        const response = await axios.post<CreateInventoryItemResponse>('/api/inventory', body);

        return toItem(response.data.data);
    },

    updateInventoryItem: async (itemId: number, request: updateInventoryItemFormInterface): Promise<Item> => {
        const body: UpdateInventoryItemRequest = {
            title: request.title,
            condition: request.condition,
            condition_note: request.conditionNote,
            length_mm: request.lengthMm,
            width_mm: request.widthMm,
            height_mm: request.heightMm,
            weight_gm: request.weightGm,
        };
        const response = await axios.put<UpdateInventoryItemResponse>(`/api/inventory/${itemId}`, body);

        return toItem(response.data.item);
    },

    updateInventoryItemAreDimensionsLocked: async (itemId: number, areDimensionsLocked: boolean): Promise<Item> => {
        const body: UpdateInventoryItemDimensionsLockRequest = { are_dimensions_locked: areDimensionsLocked };
        const response = await axios.put<UpdateInventoryItemDimensionsLockResponse>(`/api/inventory/${itemId}/dimensions-lock`, body);

        return toItem(response.data.data);
    },

    tagInventoryItem: async (itemId: number, tagId: number): Promise<void> => {
        await axios.post<TagInventoryItemResponse>(`/api/inventory/${itemId}/tags/${tagId}`);
    },

    untagInventoryItem: async (itemId: number, tagId: number): Promise<void> => {
        await axios.delete<UntagInventoryItemResponse>(`/api/inventory/${itemId}/tags/${tagId}`);
    },

    manageServices: async (itemId: number, serviceIds: number[]): Promise<void> => {
        const body: ManageInventoryItemServicesRequest = {
            item_id: itemId,
            services: serviceIds,
        };
        await axios.post<ManageInventoryItemServicesResponse>('/api/inventory/services', body);
    },
});
